import { TimetablePrintColorOrigins, TimetablePrintColorPositions } from '@bp/planung-graphql-types';
import { Checkbox, Grid, GridColumn, GridRow, Select, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { TimetablePrintConfigurationFormType } from '../forms/TimetablePrintConfigurationForm';

export const TimetablePrintConfigurationBaseData = () => {
  const { t } = useTranslation();

  const { setFieldValue, values } = useFormikContext<TimetablePrintConfigurationFormType>();

  const subjectColor = '#C0CEF2';
  const teacherColor = '#F2C1C0';
  const roomColor = '#C8F2C0';
  const classColor = '#FAECB9';
  const white = '#f7f7f7';

  const originOptions: SelectOptionType[] = [
    {
      label: t('common.none'),
      value: TimetablePrintColorOrigins.None,
    },
    {
      label: t('rooms.title', { count: 1 }),
      value: TimetablePrintColorOrigins.Room,
    },
    {
      label: t('subject.title', { count: 1 }),
      value: TimetablePrintColorOrigins.Subject,
    },
    {
      label: t('persons.title', { count: 1 }),
      value: TimetablePrintColorOrigins.Teacher,
    },
    {
      label: t('classes.title', { count: 1 }),
      value: TimetablePrintColorOrigins.Class,
    },
  ];

  const positionOptions: SelectOptionType[] = [
    {
      label: t('timetable.configuration.options.full'),
      value: TimetablePrintColorPositions.Full,
    },
    {
      label: t('timetable.configuration.options.top'),
      value: TimetablePrintColorPositions.Top,
    },
    {
      label: t('timetable.configuration.options.right'),
      value: TimetablePrintColorPositions.Right,
    },
    {
      label: t('timetable.configuration.options.bottom'),
      value: TimetablePrintColorPositions.Bottom,
    },
    {
      label: t('timetable.configuration.options.left'),
      value: TimetablePrintColorPositions.Left,
    },
    {
      label: t('common.none'),
      value: TimetablePrintColorPositions.None,
    },
  ];

  async function handleColorOriginChange(origin: TimetablePrintColorOrigins) {
    let color = subjectColor;
    switch (origin) {
      case TimetablePrintColorOrigins.Teacher:
        color = teacherColor;
        break;
      case TimetablePrintColorOrigins.Room:
        color = roomColor;
        break;
      case TimetablePrintColorOrigins.Class:
        color = classColor;
        break;
      case TimetablePrintColorOrigins.Subject:
        color = subjectColor;
        break;
      case TimetablePrintColorOrigins.None:
        color = white;
        break;
    }
    await setFieldValue('colorOrigin', origin);
    await setFieldValue('color', color);
  }

  return (
    <Grid useFormGap>
      <GridRow spacingBottom='none' spacingTop='none'>
        <GridColumn width={6}>
          <Select
            options={originOptions}
            defaultValue={originOptions.find((o) => o.value === values.colorOrigin)}
            onChange={async (option) =>
              await handleColorOriginChange((option as SelectOptionType).value as TimetablePrintColorOrigins)
            }
            name='color-origin'
            label={t('timetable.configuration.colorOrigin')}
          />
        </GridColumn>
        <GridColumn width={6}>
          <Select
            disabled={values.colorOrigin === TimetablePrintColorOrigins.None}
            options={positionOptions}
            defaultValue={positionOptions.find((o) => o.value === values.colorPosition)}
            onChange={async (option) =>
              await setFieldValue('colorPosition', (option as SelectOptionType).value as TimetablePrintColorPositions)
            }
            name='color-position'
            label={t('timetable.configuration.colorPosition')}
          />
        </GridColumn>
      </GridRow>

      <GridRow spacingTop='none' spacingBottom='none'>
        <GridColumn width={3}>
          <Checkbox
            name='print-classes'
            checked={values.printClasses}
            onChange={async (event) => await setFieldValue('printClasses', event.target.checked)}
            label={t('timetable.configuration.printClasses')}
          />
          <Checkbox
            name='scale-text'
            checked={values.scaleText}
            onChange={async (event) => await setFieldValue('scaleText', event.target.checked)}
            label={t('timetable.configuration.scaleText')}
          />
          <Checkbox
            name='time-horizontal'
            checked={values.timeHorizontal}
            onChange={async (event) => await setFieldValue('timeHorizontal', event.target.checked)}
            label={t('timetable.configuration.timeHorizontal')}
          />
        </GridColumn>

        <GridColumn width={3}>
          <Checkbox
            name='exact-minute'
            checked={values.exactMinute}
            onChange={async (event) => await setFieldValue('exactMinute', event.target.checked)}
            label={t('timetable.configuration.exactMinute')}
          />
          <Checkbox
            name='print-subtitle'
            checked={values.printSubtitle}
            onChange={async (event) => await setFieldValue('printSubtitle', event.target.checked)}
            label={t('timetable.configuration.printSubtitle')}
          />
        </GridColumn>

        <GridColumn width={3} offset='right'>
          <Checkbox
            name='large-font'
            checked={values.largeFont}
            onChange={async (event) => await setFieldValue('largeFont', event.target.checked)}
            label={t('timetable.configuration.largeFont')}
          />
          <Checkbox
            name='print-logo'
            disabled={true}
            checked={values.printLogo}
            onChange={async (event) => await setFieldValue('printLogo', event.target.checked)}
            label={t('timetable.configuration.printLogo')}
          />
        </GridColumn>

        <GridColumn width={3}>
          <Checkbox
            name='show-school-weeks'
            checked={values.showSchoolWeeks}
            onChange={async (event) => await setFieldValue('showSchoolWeeks', event.target.checked)}
            label={t('timetable.configuration.showSchoolWeeks')}
          />
          <Checkbox
            name='show-calendar-weeks'
            checked={values.showCalendarWeeks}
            onChange={async (event) => await setFieldValue('showCalendarWeeks', event.target.checked)}
            label={t('timetable.configuration.showCalendarWeeks')}
          />
          <Checkbox
            name='show-week-school-days'
            checked={values.showWeekSchoolDays}
            onChange={async (event) => await setFieldValue('showWeekSchoolDays', event.target.checked)}
            label={t('timetable.configuration.showWeekSchoolDays')}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
